import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
  Paper,
  Avatar,
  Chip,
  Fade,
  Zoom,
  Divider,
  Tooltip,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { capitalizeFirstLetter } from '../../utils/helpers';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

export default function ChatDialog({
  open,
  onClose,
  messages,
  userId,
  friendId,
  friendName,
  onDeleteMessage,
  onClearHistory,
}) {
  const handleDeleteMessage = async (msgId) => {
    const result = await Swal.fire({
      title: 'Delete this message?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'swal2-popup-modern',
        confirmButton: 'swal2-confirm-modern',
        cancelButton: 'swal2-cancel-modern'
      }
    });

    if (result.isConfirmed) {
      onDeleteMessage(msgId);
      toast.success('Message deleted successfully');
    }
  };

  const handleClearHistory = async () => {
    const result = await Swal.fire({
      title: 'Clear chat history?',
      text: 'All messages will be deleted permanently. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, clear all!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'swal2-popup-modern',
        confirmButton: 'swal2-confirm-modern',
        cancelButton: 'swal2-cancel-modern'
      }
    });

    if (result.isConfirmed) {
      onClearHistory(userId, friendId);
      toast.success('Chat history cleared successfully');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(145deg, #1a202c 0%, #2d3748 100%)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden'
        }
      }}
    >
      <Fade in={open} timeout={300}>
        <Box>
          {/* Modern Header */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.3)'
                  }}
                >
                  <ChatIcon sx={{ fontSize: 24, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1.5 }}>
                    Chat with {friendName || 'Friend'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {messages?.length || 0} messages
                  </Typography>
                </Box>
              </Stack>
              
              <Stack direction="row" spacing={1}>
                {messages && messages.length > 0 && (
                  <Tooltip title="Clear all messages" placement="bottom">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ClearAllIcon />}
                      onClick={handleClearHistory}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      Clear All
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Close chat" placement="bottom">
                  <IconButton
                    onClick={onClose}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>

          {/* Chat Messages Area */}
          <DialogContent
            sx={{
              p: 0,
              maxHeight: 500,
              minHeight: 400,
              overflowY: 'auto',
              background: 'linear-gradient(145deg, #1a202c 0%, #2d3748 100%)',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {messages && messages.length > 0 ? (
              <Box sx={{ p: 3 }}>
                <Stack spacing={2}>
                  {messages.map((msg, index) => {
                    const isSender = msg?.senderId?._id === userId;
                    const senderName = isSender
                      ? capitalizeFirstLetter(msg?.senderId?.username) || 'You'
                      : capitalizeFirstLetter(msg?.senderId?.username) || 'Friend';

                    return (
                      <Zoom in={true} timeout={300 + (index * 100)} key={msg._id}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems={isSender ? 'flex-end' : 'flex-start'}
                          sx={{ mb: 2 }}
                        >
                          {/* Sender Name */}
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                background: isSender 
                                  ? 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)'
                                  : 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                                fontSize: '12px'
                              }}
                            >
                              <PersonIcon sx={{ fontSize: 14 }} />
                            </Avatar>
                            <Typography
                              variant="caption"
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}
                            >
                              {senderName}
                            </Typography>
                          </Stack>

                          {/* Message Bubble */}
                          <Box display="flex" alignItems="flex-end" flexDirection={isSender ? 'row-reverse' : 'row'}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                maxWidth: '70%',
                                background: isSender 
                                  ? 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)'
                                  : 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid',
                                borderColor: isSender 
                                  ? 'rgba(0, 161, 221, 0.3)'
                                  : 'rgba(255, 255, 255, 0.1)',
                                position: 'relative',
                                '&::before': isSender ? {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  right: -8,
                                  width: 0,
                                  height: 0,
                                  borderLeft: '8px solid #42a5f5',
                                  borderTop: '8px solid transparent',
                                  borderBottom: '8px solid transparent',
                                } : {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  left: -8,
                                  width: 0,
                                  height: 0,
                                  borderRight: '8px solid rgba(255, 255, 255, 0.05)',
                                  borderTop: '8px solid transparent',
                                  borderBottom: '8px solid transparent',
                                }
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: isSender ? 'white' : 'text.primary',
                                  fontWeight: 400,
                                  lineHeight: 1.4
                                }}
                              >
                                {msg.message}
                              </Typography>
                            </Paper>

                            {/* Delete Button */}
                            <Tooltip title="Delete message" placement="top">
                              <IconButton
                                onClick={() => handleDeleteMessage(msg._id)}
                                size="small"
                                sx={{
                                  ml: isSender ? 0 : 1,
                                  mr: isSender ? 1 : 0,
                                  color: 'text.secondary',
                                  opacity: 0.7,
                                  '&:hover': {
                                    color: 'error.main',
                                    opacity: 1,
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Zoom>
                    );
                  })}
                </Stack>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: 300,
                  textAlign: 'center',
                  p: 4
                }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 3,
                    background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                    boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                  }}
                >
                  <ChatIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  No messages yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start a conversation with {friendName || 'this friend'}
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Box>
      </Fade>
    </Dialog>
  );
}
